import { ChangeEvent, Dispatch, useReducer } from 'react'
import styled from '@emotion/styled'
import {
  DialogTitle,
  DialogContent,
  Typography,
  CircularProgress,
  DialogActions,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Box,
  Dialog,
  Radio,
} from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { DarkButton } from '@components'
import { useExportDatasetData } from '@datasets/hooks'
import { colors } from '@global/constants'
import { DownloadOption } from '../types'
import { reducer } from './reducer'
import * as css from './styles'
import { ActionType, FileType, State } from './types'
import { t } from '@lingui/core/macro'
import { RootState } from '@redux/store'

const FILE_TYPES = ['CSV', 'XLSX', 'JSON'] as const satisfies FileType[]

interface Props {
  open: boolean
  setOpen: Dispatch<boolean>
  downloadOptions: DownloadOption[]
  downloadOptionsLoading: boolean
  datasetRowCount: number
}

const HeightLimitedImg = styled.img`
  max-height: 24px;
`

interface CircularProgressContainerProps {
  open: boolean
}

const CircularProgressContainer = styled.div<CircularProgressContainerProps>`
  display: ${props => (props.open ? 'flex' : 'none')};
  justifycontent: 'center';
  alignitems: 'center';
  height: '30vh';
`

interface ExportDatasetDataDialogRouterParams {
  id: string
}

export const ExportDatasetDataDialog = ({
  open,
  setOpen,
  downloadOptions,
  downloadOptionsLoading,
  datasetRowCount,
}: Props) => {
  const placeholder: DownloadOption = {
    id: 'current',
    name: t`Current View`,
    type: 'hide',
    exportConfigurationId: null,
  }
  const initialState: State = {
    selected: downloadOptions[0] ?? placeholder,
    fileType: 'CSV',
    email: '',
  }

  const params = useParams<ExportDatasetDataDialogRouterParams>()
  const template = useSelector(
    (state: RootState) => state.Submissions.templatesById[Number(params.id)],
  )

  const [{ selected, fileType }, dispatch] = useReducer(reducer, initialState)
  const optionWithRelationships = downloadOptions.find(
    option => option.id === 'all-data-relationship',
  )
  const exportAll = selected.id === downloadOptions[0]?.id
  const exportAllWithRelationships = selected.id === optionWithRelationships?.id

  const { isInitialLoading: isLoading, refetch } = useExportDatasetData(
    exportAll,
    Number(selected.id),
    fileType,
    template?.name ?? '',
    exportAllWithRelationships,
  )

  if (downloadOptionsLoading) {
    return (
      <CircularProgressContainer open={open}>
        <CircularProgress color="primary" />
      </CircularProgressContainer>
    )
  }

  return (
    <Dialog onClose={handleClose} open={open}>
      <DialogTitle css={css.dialogTitle}>
        <Typography variant="h3" css={css.title}>
          {t`Export data`}
        </Typography>
      </DialogTitle>
      <DialogContent css={css.content}>
        <Autocomplete
          id="download-options"
          disableClearable
          sx={{
            margin: '25px 0 0',
          }}
          options={downloadOptions}
          getOptionLabel={option => option.name}
          value={selected}
          onChange={(_, value) => {
            handleChangeSelected({ option: value })
          }}
          renderOption={(props, option) => (
            <Box
              sx={{
                paddingLeft: '14px',
                display: 'flex',
                gap: '8px',
              }}
              component="li"
              {...props}
            >
              {option.type !== 'hide' ? (
                <HeightLimitedImg
                  src={`https://claravine-public-prod.s3-us-west-2.amazonaws.com/images/${option.type}.png`}
                />
              ) : null}
              <div>{option.name}</div>
            </Box>
          )}
          renderInput={params => (
            <TextField
              {...params}
              label={t`File configuration`}
              variant="outlined"
              sx={{
                '.MuiInputBase-root': {
                  padding: '8px 14px !important',
                },
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment:
                  (selected.type !== 'hide' && (
                    <HeightLimitedImg
                      src={`https://claravine-public-prod.s3-us-west-2.amazonaws.com/images/${selected.type}.png`}
                    />
                  )) ||
                  null,
              }}
            />
          )}
        />
        <css.FileTypeGroup>
          <FormControl component="fieldset" css={css.formControl}>
            <FormLabel component="legend">{t`Select file type:`}</FormLabel>
            <RadioGroup
              aria-label="file-type"
              defaultValue={FILE_TYPES[0]}
              name="file-type"
              onChange={handleChangeFileType}
              value={fileType}
            >
              {FILE_TYPES.map(fileType => (
                <FormControlLabel
                  key={fileType}
                  label={
                    <span css={css.formLabel}>
                      {fileType}
                      {fileType === 'XLSX' && datasetRowCount > 1000000
                        ? t`(Disabled - limit 1,000,000 rows)`
                        : ''}
                    </span>
                  }
                  value={fileType}
                  control={
                    <Radio
                      {...getControlProps(fileType, datasetRowCount)}
                      sx={{
                        '&.Mui-checked': {
                          color: colors.blue.smokey,
                        },
                      }}
                    />
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>
        </css.FileTypeGroup>
      </DialogContent>
      <DialogActions>
        <DarkButton onClick={handleClose}>{t`Cancel`}</DarkButton>
        <css.ButtonContainer>
          <DarkButton onClick={handleConfirm} disabled={isLoading} contained>
            {t`Export`}
          </DarkButton>
          {isLoading && <CircularProgress size={20} css={css.buttonProgress} />}
        </css.ButtonContainer>
      </DialogActions>
    </Dialog>
  )

  function handleClose(): void {
    setOpen(false)
    resetState()
  }

  async function handleConfirm(): Promise<void> {
    const { data } = await refetch()

    if (!data) return

    setOpen(false)
    resetState()
  }

  function getControlProps(value: FileType, datasetRowCount: number) {
    const controlProps = {
      checked: fileType === value,
      value,
      name: 'color-radio-button-demo',
      inputProps: { 'aria-label': value },
      onChange: () => {
        dispatch({ type: ActionType.setFileType, payload: { fileType: value } })
      },
      disabled: false,
    }
    if (value === 'XLSX' && datasetRowCount > 1000000) {
      controlProps.disabled = true
    }
    return controlProps
  }

  function resetState(): void {
    dispatch({
      type: ActionType.resetState,
      payload: {
        selected: initialState.selected,
        fileType: initialState.fileType,
        email: initialState.email,
      },
    })
  }

  function handleChangeSelected({ option }: { option: DownloadOption | null }): void {
    if (!option) return

    dispatch({ type: ActionType.setSelected, payload: { selected: option } })
  }

  function handleChangeFileType(e: ChangeEvent<HTMLInputElement>): void {
    dispatch({ type: ActionType.setFileType, payload: { fileType: e.target.value as FileType } })
  }
}
